import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, SafeAreaView, Alert, ActivityIndicator, Modal, Platform, Image } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import NLPService, { BookingIntent } from '../services/nlpService';
import { appointmentManager } from '../utils/appointmentManager';
import { appointmentRequestService } from '../services/appointmentRequestService';
import { paymentRequestService, PaymentRequest } from '../services/paymentRequestService';
import CustomerPaymentScreen from './CustomerPaymentScreen';
import UserBottomMenu from '../components/UserBottomMenu';
import { businessInvitationService } from '../services/businessInvitationService';
import { AuthManager } from '../utils/authManager';
import stripePaymentService from '../services/stripePaymentService';
import braintreeService from '../services/braintreeService';
import AppointmentRequestForm from '../components/AppointmentRequestForm';
import PaymentMethodBrandIcon from '../components/PaymentMethodBrandIcon';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  type?: 'text' | 'appointment' | 'booking' | 'info' | 'confirmation' | 'payment_request';
  data?: any;
  actions?: string[];
  timestamp: Date;
}

interface BookAPTChatScreenProps {
  onBack?: () => void;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'favorites' | 'profile') => void;
  onLogout?: () => void;
  scrollToTop?: boolean; // Trigger scroll to top when home button is pressed
}

// Process payment for chat booking
const processChatPayment = async (messageData: any, currentUser: any, paymentMethod: string, paymentData?: any) => {
  try {
    console.log('Processing chat payment with method:', paymentMethod);
    
    const paymentRequest = {
      amount: 50, // Default haircut price
      currency: 'USD',
      serviceName: 'Haircut',
      customerId: currentUser?.id || 'chat_user',
      businessId: messageData?.selectedBusiness?.id || 'chat_business',
    };

    let result;

    switch (paymentMethod) {
      case 'credit_card':
        const cardDetails = {
          number: paymentData?.cardNumber || '4242424242424242',
          expMonth: paymentData?.expMonth || 12,
          expYear: paymentData?.expYear || 2025,
          cvc: paymentData?.cvc || '123',
          name: paymentData?.cardholderName || currentUser?.name || 'Test User',
          postalCode: paymentData?.zipCode || '76010',
        };
        result = await stripePaymentService.processCreditCard(paymentRequest, cardDetails);
        break;
      
      case 'apple_pay':
        result = await stripePaymentService.processApplePay(paymentRequest);
        break;
      
      case 'google_pay':
        result = await stripePaymentService.processGooglePay(paymentRequest);
        break;
      
      case 'venmo':
        result = await braintreeService.processVenmoPayment({
          amount: paymentRequest.amount,
          serviceName: paymentRequest.serviceName,
          customerId: paymentRequest.customerId,
          businessId: paymentRequest.businessId,
          description: `Haircut - ${messageData?.selectedBusiness?.name || 'Business'}`,
        });
        break;
      
      case 'pay_later':
        result = { success: true, transactionId: `pay_later_${Date.now()}` };
        break;
      
      default:
        result = { success: false, error: 'Unsupported payment method' };
    }
    
    console.log('Chat payment result:', result);
    return result;
  } catch (error) {
    console.error('Chat payment error:', error);
    return {
      success: false,
      error: 'Payment processing failed. Please try again.'
    };
  }
};

// Create appointment after successful payment
const createChatAppointment = async (messageData: any, currentUser: any, paymentMethod: string) => {
  try {
    // Get customer address from user object - can be string or object
    let customerAddress = 'Address not provided';
    if (currentUser?.address) {
      if (typeof currentUser.address === 'string') {
        customerAddress = currentUser.address;
      } else if (typeof currentUser.address === 'object') {
        const addr = currentUser.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string };
        const parts = [
          addr.street,
          addr.city,
          addr.state,
          addr.zipcode
        ].filter(Boolean);
        customerAddress = parts.length > 0 ? parts.join(', ') : 'Address not provided';
      }
    }
    
    const appointmentData = {
      customerName: currentUser?.name || 'Current User',
      phone: currentUser?.phone || '',
      email: currentUser?.email || 'user@example.com',
      address: customerAddress, // Always use customer address, never business address
      serviceType: 'Haircut',
      appointmentDate: messageData.selectedTime.includes('Tomorrow') ? 
        new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0],
      appointmentTime: messageData.selectedTime.includes('10AM') ? '10:00 AM' : '2:00 PM',
      status: 'confirmed' as const,
      specialInstructions: `Booked via chat - ${paymentMethod === 'pay_later' ? 'Confirm booking' : 'Payment processed'}`,
      // Business information for user view
      businessName: messageData.selectedBusiness?.name,
      businessAddress: messageData.selectedBusiness?.address,
      businessPhone: messageData.selectedBusiness?.phone,
      businessEmail: messageData.selectedBusiness?.email,
    };

    console.log('🔍 Creating appointment from chat:', appointmentData);
    
    const savedAppointment = await appointmentManager.saveAppointment(appointmentData);
    console.log('✅ Appointment saved successfully from chat booking:', savedAppointment);
    
    return savedAppointment;
  } catch (error) {
    console.error('Error creating chat appointment:', error);
    throw error;
  }
};

const BookAPTChatScreen: React.FC<BookAPTChatScreenProps> = ({ onBack, onTabPress, onLogout, scrollToTop }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hi! We're here to do the heavy lifting so you don't have to. We'll book your appointments with local businesses—no calls, no calendar checking, no hassle. Save time while we find the best availability, coordinate directly with businesses, and confirm your appointments. One place for all your appointment needs. What would you like to book today?",
      isUser: false,
      type: 'text',
      actions: ['🚀 30 Sec Booking', '💬 Chat with me'],
      timestamp: new Date(),
    },
    {
      id: '2',
      text: "Here are some examples of what you can ask me:",
      isUser: false,
      type: 'text',
      actions: ['Book a haircut for tomorrow', 'I need a massage this weekend', "What's available at 2 PM tomorrow for car oil change?"],
      timestamp: new Date(),
    },
    {
      id: '3',
      text: "Just type naturally and I'll understand!",
      isUser: false,
      type: 'text',
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [currentPaymentRequest, setCurrentPaymentRequest] = useState<PaymentRequest | null>(null);
  const [showAppointmentRequestForm, setShowAppointmentRequestForm] = useState(false);
  const [selectedBusinessForRequest, setSelectedBusinessForRequest] = useState<any>(null);
  
  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    cardholderName: '',
    zipCode: '',
  });
  const [pendingBookingData, setPendingBookingData] = useState<any>(null);
  
  const scrollViewRef = useRef<ScrollView>(null);

  // Available payment methods
  const availablePaymentMethods = [
    { id: 'credit_card', name: 'Credit/Debit Card' },
    { id: 'apple_pay', name: 'Apple Pay' },
    { id: 'google_pay', name: 'Google Pay' },
    { id: 'venmo', name: 'Venmo' },
    { id: 'pay_later', name: 'Confirm booking' },
  ].filter(method => {
    // Filter based on platform capabilities
    if (method.id === 'apple_pay' && Platform.OS !== 'ios') return false;
    if (method.id === 'google_pay' && Platform.OS !== 'android') return false;
    return true;
  });

  // Helper function to scroll to end
  const scrollToEnd = () => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  const handleSendMessage = async () => {
    if (inputText.trim() === '') return;

    const userMessage = inputText.trim();
    const userMsg: Message = {
      id: Date.now().toString(),
      text: userMessage,
      isUser: true,
      type: 'text',
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);

    try {
      // Process message with NLP
      const intent = await NLPService.processMessage(userMessage);
      const response = await NLPService.generateResponse(intent);
      
      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: response.text,
        isUser: false,
        type: response.type,
        data: response.data,
        actions: response.actions,
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('Error processing message:', error);
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, I encountered an error. Please try again or contact us directly.",
        isUser: false,
        type: 'text',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleActionPress = async (action: string, messageData?: any) => {
    console.log('Action pressed:', action, 'Message data:', messageData);
    
    // Handle action buttons
    if (action === '🚀 30 Sec Booking') {
      // Navigate to 30-second booking
      if (onTabPress) {
        onTabPress('dashboard'); // This will trigger the 30-second booking in the dashboard
      }
    } else if (action === '💬 Chat with me') {
      // Just focus on the input field - no action needed
      return;
    } else if (action === 'Book a haircut for tomorrow' || 
               action === 'I need a massage this weekend' || 
               action === "What's available at 2 PM tomorrow for car oil change?") {
      // Put the example text in the input box (don't send yet)
      setInputText(action);
    } else if (action === 'I need a massage') {
      setInputText('I need a massage this weekend');
    } else if (action === 'Show me today\'s slots') {
      setInputText('What slots are available today?');
    } else if (action === 'Cancel my booking') {
      setInputText('Cancel my appointment');
    } else if (action === 'Help me reschedule') {
      setInputText('Help me reschedule my appointment');
    } else if (action === 'Select Time') {
      // Handle Select Time action - show available time slots
      const service = messageData?.service || 'service';
      const botMessage: Message = {
        id: Date.now().toString(),
        text: `Here are available time slots for ${service}:\n\n📅 Today: 2PM, 4PM, 6PM\n📅 Tomorrow: 10AM, 2PM, 5PM\n📅 Weekend: Sat 9AM, Sun 11AM\n\nWhich time works for you?`,
        isUser: false,
        type: 'booking',
        data: messageData,
        actions: ['Today 2PM', 'Today 4PM', 'Tomorrow 10AM', 'Tomorrow 2PM', 'Use 30 Sec Booking'],
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action === 'Provide Details') {
      // Handle Provide Details action - show form or ask for more info
      const service = messageData?.service || 'appointment';
      const botMessage: Message = {
        id: Date.now().toString(),
        text: `Perfect! Let me get some more details for your ${service} appointment. Please tell me:\n\n• Preferred date and time\n• Any special requests or notes\n• Your contact information (if not on file)`,
        isUser: false,
        type: 'info',
        actions: ['Check Availability', 'View Services'],
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action === 'View Services') {
      // Handle View Services action - show all services
      setInputText('What services do you offer?');
      // Trigger the send message automatically after a brief delay
      setTimeout(() => {
        handleSendMessage();
      }, 100);
    } else if (action === 'View Time Slots') {
      // Handle View Time Slots action
      const service = messageData?.service || 'service';
      const botMessage: Message = {
        id: Date.now().toString(),
        text: `Here are available time slots for ${service}:\n\n📅 Today:\n• 2:00 PM - 3:00 PM\n• 4:00 PM - 5:00 PM\n• 6:00 PM - 7:00 PM\n\n📅 Tomorrow:\n• 10:00 AM - 11:00 AM\n• 2:00 PM - 3:00 PM\n• 5:00 PM - 6:00 PM\n\n📅 This Weekend:\n• Saturday: 9:00 AM - 10:00 AM, 2:00 PM - 3:00 PM\n• Sunday: 11:00 AM - 12:00 PM, 3:00 PM - 4:00 PM\n\nSelect a time slot to book:`,
        isUser: false,
        type: 'booking',
        data: messageData,
        actions: ['Today 2PM', 'Today 4PM', 'Tomorrow 10AM', 'Tomorrow 2PM', 'Use 30 Sec Booking'],
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action.startsWith('Today ') || action.startsWith('Tomorrow ') || action.startsWith('Saturday ') || action.startsWith('Sunday ')) {
      // Handle time slot selection
      // Note: This should only be reached for businesses ON BookAPT
      // Businesses NOT on BookAPT should have shown the form immediately
      const selectedTime = action;
      const service = messageData?.service || 'service';
      const business = messageData?.selectedBusiness || messageData?.nearbyBusinesses?.[0];
      const isBusinessOnBookAPT = business?.isOnBookAPT === true;
      
      // Double-check - if somehow we got here for a business not on BookAPT, show form
      if (!isBusinessOnBookAPT) {
        console.log('⚠️ Business not on BookAPT, showing appointment request form...');
        
        // Extract time from selected action
        let prefilledTime = '';
        if (selectedTime.includes('10AM') || selectedTime.includes('10:00 AM')) prefilledTime = '10:00 AM';
        else if (selectedTime.includes('2PM') || selectedTime.includes('2:00 PM')) prefilledTime = '2:00 PM';
        else if (selectedTime.includes('4PM') || selectedTime.includes('4:00 PM')) prefilledTime = '4:00 PM';
        else if (selectedTime.includes('5PM') || selectedTime.includes('5:00 PM')) prefilledTime = '5:00 PM';
        else if (selectedTime.includes('6PM') || selectedTime.includes('6:00 PM')) prefilledTime = '6:00 PM';
        
        // Extract date
        let prefilledDate = '';
        if (selectedTime.includes('Tomorrow')) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          prefilledDate = tomorrow.toISOString().split('T')[0];
        } else if (selectedTime.includes('Today')) {
          prefilledDate = new Date().toISOString().split('T')[0];
        }
        
        setSelectedBusinessForRequest({
          ...business,
          prefillService: service,
          prefillTime: prefilledTime,
          prefillDate: prefilledDate,
        });
        setShowAppointmentRequestForm(true);
        
        const botMessage: Message = {
          id: Date.now().toString(),
          text: `I see you've selected **${business?.name || 'this business'}** for ${selectedTime}. They're not on BookAPT yet, so I'll help you request an appointment. Please fill out the form with your details, and we'll contact them on your behalf!`,
          isUser: false,
          type: 'info',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);
        scrollToEnd();
        return; // Don't show confirmation - show form instead
      }
      
      // Business IS on BookAPT - show confirmation
      const botMessage: Message = {
        id: Date.now().toString(),
        text: `Great! Let me confirm your booking:\n\n📋 **Booking Summary:**\n• Service: ${service}\n• Business: ${business?.name || 'Selected business'}\n• Time: ${selectedTime}\n• Price: ${business?.price || 'TBD'}\n• Duration: ${business?.duration || 'TBD'}\n\n✅ Ready to confirm your booking?`,
        isUser: false,
        type: 'confirmation',
        data: {
          ...messageData,
          selectedTime,
          selectedBusiness: business,
        },
        actions: [
          'Confirm & Pay Now',
          ...(business?.paymentRequiredAtBooking ? [] : ['Confirm booking']),
          'Change Time',
          'Cancel',
        ],
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (
      action === 'Confirm & Pay Now' ||
      action === 'Confirm booking'
    ) {
      // Handle booking confirmation
      const isPayNow = action === 'Confirm & Pay Now';
      const paymentType = isPayNow ? 'now' : 'later';
      
      try {
        // Get current user data
        const currentUser = await AuthManager.getSavedUser();
        
        if (paymentType === 'now') {
          // Store booking data and show payment modal
          setPendingBookingData({ messageData, currentUser });
          setShowPaymentModal(true);
          return; // Don't proceed with booking until payment is processed
        }
        
        // Check if business is on BookAPT
        const selectedBusiness = messageData?.selectedBusiness;
        const isBusinessOnBookAPT = selectedBusiness?.isOnBookAPT === true;
        
        if (selectedBusiness && messageData?.selectedTime) {
          // Extract date and time
          const appointmentDate = messageData.selectedTime.includes('Tomorrow') ? 
            new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : 
            new Date().toISOString().split('T')[0];
          
          let appointmentTime = '2:00 PM'; // Default
          if (messageData.selectedTime.includes('10AM')) appointmentTime = '10:00 AM';
          else if (messageData.selectedTime.includes('2PM')) appointmentTime = '2:00 PM';
          else if (messageData.selectedTime.includes('4PM')) appointmentTime = '4:00 PM';
          else if (messageData.selectedTime.includes('5PM')) appointmentTime = '5:00 PM';
          else if (messageData.selectedTime.includes('6PM')) appointmentTime = '6:00 PM';
          
          const serviceType = messageData?.service || 'Service';
          
          if (!isBusinessOnBookAPT) {
            // Business is NOT on BookAPT - show appointment request form (middleman)
            console.log('⚠️ Business not on BookAPT, showing appointment request form...');
            
            // Store the selected business and show the form
            setSelectedBusinessForRequest(selectedBusiness);
            setShowAppointmentRequestForm(true);
            
            // Add a message explaining what's happening
            const botMessage: Message = {
              id: Date.now().toString(),
              text: `I see you want to book with **${selectedBusiness.name}**. They're not on BookAPT yet, so I'll help you request an appointment. Please fill out the form to provide your details, and we'll contact them on your behalf!`,
              isUser: false,
              type: 'info',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, botMessage]);
            scrollToEnd();
            
            return; // Exit early - don't create regular appointment
          } else {
            // Business IS on BookAPT - create appointment normally
            console.log('✅ Business is on BookAPT, creating appointment...');
            
            const appointmentData = {
              customerName: currentUser?.name || 'Current User',
              phone: currentUser?.phone || '',
              email: currentUser?.email || '',
              address: selectedBusiness.address || '',
              serviceType: serviceType,
              appointmentDate: appointmentDate,
              appointmentTime: appointmentTime,
              status: 'confirmed' as const,
              specialInstructions: `Booked via chat - ${isPayNow ? 'Payment processed' : 'Confirm booking'}`
            };

            console.log('🔍 Creating appointment from chat:', appointmentData);
            
            try {
              const savedAppointment = await appointmentManager.saveAppointment(appointmentData);
              console.log('✅ Appointment saved successfully from chat booking:', savedAppointment);
            } catch (error: any) {
              if (error.message === 'APPOINTMENT_LIMIT_REACHED' && error.limitCheck) {
                Alert.alert(
                  'Appointment Limit Reached',
                  error.limitCheck.upgradeMessage || `You've reached your ${error.limitCheck.limit} appointment limit. Please upgrade to continue booking.`,
                  [
                    { text: 'Cancel', style: 'cancel' },
                    { text: 'Upgrade', onPress: () => {
                      // Navigate to subscription screen
                      // You'll need to add navigation logic here
                    }}
                  ]
                );
                return;
              }
              throw error;
            }
          }
        }
        
        const botMessage: Message = {
          id: Date.now().toString(),
          text: `🎉 Booking Confirmed!\n\nYour appointment has been successfully booked.\n\n${isPayNow ? '💳 Payment processed successfully!' : '📌 Payment will be collected after service.'}\n\nYou'll receive a confirmation email and SMS shortly.\n\nWhat else can I help you with?`,
          isUser: false,
          type: 'confirmation',
          timestamp: new Date(),
        };
        
        setMessages((prev) => [...prev, botMessage]);
        scrollToEnd();
        
        // Show success alert and navigate to appointments
        if (isPayNow) {
          setTimeout(() => {
            Alert.alert(
              'Payment Successful', 
              'Your booking is confirmed and payment has been processed!',
              [
                {
                  text: 'View Appointments',
                  onPress: () => {
                    if (onTabPress) {
                      onTabPress('appointments');
                    }
                  }
                }
              ]
            );
          }, 500);
        } else {
          setTimeout(() => {
            Alert.alert(
              'Booking Confirmed', 
              'You can pay after the service is completed.',
              [
                {
                  text: 'View Appointments',
                  onPress: () => {
                    if (onTabPress) {
                      onTabPress('appointments');
                    }
                  }
                }
              ]
            );
          }, 500);
        }
      } catch (error) {
        console.error('❌ Error creating appointment from chat:', error);
        
        const errorMessage: Message = {
          id: Date.now().toString(),
          text: `❌ Sorry, there was an error creating your appointment. Please try again or contact support.`,
          isUser: false,
          type: 'info',
          timestamp: new Date(),
        };
        
        setMessages((prev) => [...prev, errorMessage]);
        scrollToEnd();
      }
    } else if (action === 'Change Time') {
      // Go back to time selection
      const botMessage: Message = {
        id: Date.now().toString(),
        text: 'No problem! Here are the available times again:\n\n📅 Today: 2PM, 4PM, 6PM\n📅 Tomorrow: 10AM, 2PM, 5PM\n📅 Weekend: Sat 9AM, Sun 11AM\n\nWhich time works better for you?',
        isUser: false,
        type: 'booking',
        data: messageData,
        actions: ['Today 2PM', 'Today 4PM', 'Tomorrow 10AM', 'Tomorrow 2PM', 'Use 30 Sec Booking'],
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action.match(/^\d+\.\s/)) {
      // Handle business selection (format: "1. Business Name")
      const businesses = messageData?.nearbyBusinesses || [];
      const businessIndex = parseInt(action.charAt(0)) - 1;
      const selectedBusiness = businesses[businessIndex];
      
      if (selectedBusiness) {
        const service = messageData?.service || 'service';
        const isBusinessOnBookAPT = selectedBusiness?.isOnBookAPT === true;
        
        if (!isBusinessOnBookAPT) {
          // Business is NOT on BookAPT - show appointment request form immediately
          console.log('⚠️ Business not on BookAPT, showing appointment request form immediately...');
          
          // Store business and service info for the form
          setSelectedBusinessForRequest({
            ...selectedBusiness,
            prefillService: service, // Pass service to pre-fill
            preferredDate: messageData?.date, // Pass preferred date if available
          });
          setShowAppointmentRequestForm(true);
          
          const botMessage: Message = {
            id: Date.now().toString(),
            text: `I see you want to book with **${selectedBusiness.name}**. They're not on BookAPT yet, so I'll help you request an appointment. Please fill out the form with your details, and we'll contact them on your behalf!`,
            isUser: false,
            type: 'info',
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, botMessage]);
          scrollToEnd();
          return; // Exit early - don't show time slots
        }
        
        // Business IS on BookAPT - show time slots
        const dateText = messageData?.date && messageData.date !== 'flexible' ? ` ${messageData.date}` : '';
        
        const botMessage: Message = {
          id: Date.now().toString(),
          text: `Excellent choice! You've selected **${selectedBusiness.name}**.\n\n📋 Service: ${service}\n⭐ Rating: ${selectedBusiness.rating}\n💰 Price: ${selectedBusiness.price}\n⏰ Duration: ${selectedBusiness.duration}${dateText ? `\n📅 Preferred: ${dateText}` : ''}\n\nHere are the available time slots:\n\n📅 **Today:**\n• 2:00 PM\n• 4:00 PM\n• 6:00 PM\n\n📅 **Tomorrow:**\n• 10:00 AM\n• 2:00 PM\n• 5:00 PM\n\nWhich time works best for you?`,
          isUser: false,
          type: 'booking',
          data: {
            ...messageData,
            selectedBusiness,
            step: 'select_time',
          },
          actions: ['Today 2PM', 'Today 4PM', 'Tomorrow 10AM', 'Tomorrow 2PM', 'Tomorrow 5PM'],
          timestamp: new Date(),
        };
        
        setMessages((prev) => [...prev, botMessage]);
        scrollToEnd();
      }
    } else if (action === 'Use 30 Sec Booking') {
      // Navigate to 30-second booking
      if (onTabPress) {
        onTabPress('dashboard'); // This will trigger the 30-second booking in the dashboard
      }
    } else if (action === 'View All Categories') {
      // Navigate to 30 sec booking to show all categories
      if (onTabPress) {
        onTabPress('dashboard');
      }
    } else if (action === 'View More Details') {
      // Handle View More Details action
      const businesses = messageData?.nearbyBusinesses || [];
      if (businesses.length > 0) {
        let detailsText = 'Here are more details:\n\n';
        businesses.forEach((business: any, index: number) => {
          detailsText += `${index + 1}. ${business.name}\n`;
          detailsText += `   ⭐ Rating: ${business.rating}\n`;
          detailsText += `   📍 Distance: ${business.distance}\n`;
          detailsText += `   ⏰ Wait Time: ${business.waitTime}\n`;
          detailsText += `   💇 Services: ${business.services.join(', ')}\n\n`;
        });
        
        const botMessage: Message = {
          id: Date.now().toString(),
          text: detailsText,
          isUser: false,
          type: 'info',
          actions: ['View Time Slots', 'Use 30 Sec Booking'],
          timestamp: new Date(),
        };
        
        setMessages((prev) => [...prev, botMessage]);
        scrollToEnd();
      }
    } else if (action === 'Invite a Business') {
      // Handle business invitation request
      const service = messageData?.service || 'service';
      const botMessage: Message = {
        id: Date.now().toString(),
        text: `Great! I'll help you invite a business to BookAPT.\n\n📝 Please provide the business details:\n\n• **Business name**: What's the business called?\n• **Phone number**: (We'll send them an invitation)\n• **Email**: (Optional, for email invitation)\n• **Address/Location**: Where are they located?\n\nExample: "Joe's Auto Shop, (555) 123-4567, 123 Main St, Arlington TX"\n\nOnce ${service} businesses join, you'll be the first to know!`,
        isUser: false,
        type: 'info',
        data: {
          service,
          expectingBusinessInvite: true,
        },
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action === 'Browse Categories') {
      // Navigate to 30 sec booking
      if (onTabPress) {
        onTabPress('dashboard');
      }
    } else if (action === 'Request Custom Service') {
      // Handle custom service request
      const service = messageData?.service || 'custom service';
      const botMessage: Message = {
        id: Date.now().toString(),
        text: `Perfect! Let me help you request "${service}".\n\n📝 Please provide:\n\n• **Service description**: What exactly do you need?\n• **Preferred date & time**: When would you like this service?\n• **Duration estimate**: How long do you think it will take?\n• **Budget**: What's your expected price range?\n• **Additional details**: Any specific requirements?\n\nExample: "I need a custom furniture repair for my antique chair tomorrow afternoon, should take 2-3 hours, budget $150-200"\n\nType your request below and I'll send it to nearby businesses!`,
        isUser: false,
        type: 'info',
        data: {
          service,
          expectingCustomServiceDetails: true,
        },
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action === 'Browse All Businesses') {
      // Navigate to explore businesses
      if (onTabPress) {
        onTabPress('favorites'); // This will show the favorites screen with explore option
      }
    } else if (action === 'Describe Needs') {
      // Ask user to describe their needs
      const botMessage: Message = {
        id: Date.now().toString(),
        text: 'Please describe what service you need in detail:\n\n• What type of service?\n• What\'s the goal or outcome you want?\n• Any specific requirements or preferences?\n• When do you need it done?\n\nThe more details you provide, the better I can help match you with the right business!',
        isUser: false,
        type: 'info',
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action === 'Book via Chat') {
      // Guide user to provide booking details
      const botMessage: Message = {
        id: Date.now().toString(),
        text: 'Great! Please provide:\n• Your preferred date and time\n• Any special requests\n\nFor example: "Tomorrow at 2pm, I prefer a quiet room"',
        isUser: false,
        type: 'info',
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, botMessage]);
      scrollToEnd();
    } else if (action === 'Book Now' && messageData?.availableSlots) {
      // Show booking confirmation
      Alert.alert(
        'Book Appointment',
        'Would you like to book this appointment?',
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Book', 
            onPress: () => handleBookAppointment(messageData) 
          },
        ]
      );
    } else if (action === 'View All Services') {
      setInputText('What services do you offer?');
    } else if (action === 'Check Availability') {
      setInputText('What times are available?');
    } else if (action === 'Pay Now' && messageData?.paymentRequestId) {
      // Handle payment request
      handlePaymentRequest(messageData.paymentRequestId);
    } else if (action === 'View Payment Details' && messageData?.paymentRequestId) {
      // Show payment details
      handlePaymentRequest(messageData.paymentRequestId);
    }
  };

  const handleBookAppointment = async (slotData: any) => {
    try {
      // Create appointment
      const appointment = {
        id: 'apt_' + Date.now(),
        customerName: 'Chat User',
        phone: '+1 (555) 000-0000',
        email: 'chat@bookapt.io',
        address: '123 Main St, Arlington, TX',
        serviceType: 'Haircut & Styling',
        appointmentDate: new Date().toISOString().split('T')[0],
        appointmentTime: slotData.availableSlots[0],
        status: 'confirmed' as const,
        createdAt: new Date().toISOString(),
        businessOwnerId: 'test_business_1',
      };

      await appointmentManager.saveAppointment(appointment);
      
      const confirmationMsg: Message = {
        id: (Date.now() + 2).toString(),
        text: `✅ Great! I've booked your appointment for ${slotData.availableSlots[0]}. You'll receive a confirmation shortly.`,
        isUser: false,
        type: 'confirmation',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, confirmationMsg]);
    } catch (error) {
      console.error('Error booking appointment:', error);
      Alert.alert('Error', 'Failed to book appointment. Please try again.');
    }
  };

  const handlePaymentRequest = async (paymentRequestId: string) => {
    try {
      const paymentRequest = await paymentRequestService.getPaymentRequest(paymentRequestId);
      if (paymentRequest) {
        setCurrentPaymentRequest(paymentRequest);
        setShowPaymentScreen(true);
      } else {
        Alert.alert('Error', 'Payment request not found');
      }
    } catch (error) {
      console.error('Error loading payment request:', error);
      Alert.alert('Error', 'Failed to load payment request');
    }
  };

  const handlePaymentComplete = (success: boolean) => {
    setShowPaymentScreen(false);
    setCurrentPaymentRequest(null);
    
    if (success) {
      const successMsg: Message = {
        id: (Date.now() + 3).toString(),
        text: '✅ Payment completed successfully! Thank you for your payment.',
        isUser: false,
        type: 'confirmation',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, successMsg]);
    }
  };

  // Handle payment method selection in modal
  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    
    // For digital wallets and pay later, process immediately
    if (['apple_pay', 'google_pay', 'venmo', 'pay_later'].includes(methodId)) {
      handleProcessPayment(methodId);
    }
    // For credit card, user needs to fill in details
  };

  // Process payment with selected method
  const handleProcessPayment = async (methodId?: string) => {
    if (!pendingBookingData) return;
    
    const method = methodId || selectedPaymentMethod;
    if (!method) return;

    try {
      // Process payment
      const paymentResult = await processChatPayment(
        pendingBookingData.messageData,
        pendingBookingData.currentUser,
        method,
        method === 'credit_card' ? paymentData : undefined
      );

      if (paymentResult.success) {
        // Create appointment
        await createChatAppointment(
          pendingBookingData.messageData,
          pendingBookingData.currentUser,
          method
        );

        // Show success message
        const botMessage: Message = {
          id: Date.now().toString(),
          text: `🎉 Booking Confirmed!\n\nYour appointment has been successfully booked.\n\n${method === 'pay_later' ? '📌 Payment will be collected after service.' : '💳 Payment processed successfully!'}\n\nYou'll receive a confirmation email and SMS shortly.\n\nWhat else can I help you with?`,
          isUser: false,
          type: 'confirmation',
          timestamp: new Date(),
        };
        
        setMessages((prev) => [...prev, botMessage]);
        scrollToEnd();
        
        // Show success alert and navigate to appointments
        setTimeout(() => {
          Alert.alert(
            method === 'pay_later' ? 'Booking Confirmed' : 'Payment Successful', 
            method === 'pay_later' ? 'You can pay after the service is completed.' : 'Your booking is confirmed and payment has been processed!',
            [
              {
                text: 'View Appointments',
                onPress: () => {
                  if (onTabPress) {
                    onTabPress('appointments');
                  }
                }
              }
            ]
          );
        }, 500);

        // Close modal and reset state
        setShowPaymentModal(false);
        setPendingBookingData(null);
        setSelectedPaymentMethod('');
        setPaymentData({
          cardNumber: '',
          expiryDate: '',
          cvc: '',
          cardholderName: '',
          zipCode: '',
        });
      } else {
        Alert.alert('Payment Failed', paymentResult.error || 'Unable to process payment. Please try again.');
      }
    } catch (error) {
      console.error('Error processing chat payment:', error);
      Alert.alert('Error', 'An error occurred while processing your payment.');
    }
  };

  // Format card input helpers
  const formatCardNumber = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.slice(0, 19);
  };

  const formatExpiryDate = (text: string) => {
    const cleaned = text.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  // Scroll to top to show welcome message when component loads
  useEffect(() => {
    const timer = setTimeout(() => {
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({ y: 0, animated: true });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom only when new user messages arrive
  useEffect(() => {
    if (messages.length > 3) { // Only auto-scroll after initial welcome messages
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.isUser) {
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: true });
        }, 100);
      }
    }
  }, [messages]);

  // Show payment screen if payment request is active
  if (showPaymentScreen && currentPaymentRequest) {
    return (
      <CustomerPaymentScreen
        paymentRequestId={currentPaymentRequest.id}
        onPaymentComplete={handlePaymentComplete}
        onBack={() => setShowPaymentScreen(false)}
      />
    );
  }

  return (
    <View style={styles.mainContainer}>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLogoContainer}>
            <Image 
              source={require('../assets/images/bookapt-logo.png')} 
              style={styles.headerLogoImage}
              resizeMode="contain"
            />
          </View>
          <Text style={styles.headerTitle}>BookAPT Chat</Text>
        </View>

        <View style={styles.chatArea}>
          <ScrollView 
            ref={scrollViewRef}
            style={styles.messagesScrollView}
            contentContainerStyle={styles.scrollContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
          {messages.map((message) => (
            <View
              key={message.id}
              style={[
                styles.messageContainer,
                message.isUser ? styles.userMessage : styles.botMessage,
              ]}
            >
              <View style={[
                styles.messageBubble,
                message.isUser ? styles.userBubble : styles.botBubble,
              ]}>
                <Text style={[
                  styles.messageText,
                  message.isUser ? styles.userText : styles.botText,
                ]}>
                  {message.text}
                </Text>
                
                {/* Payment request card */}
                {!message.isUser && message.type === 'payment_request' && message.data && (
                  <View style={styles.paymentRequestCard}>
                    <Text style={styles.paymentRequestTitle}>💳 Payment Request</Text>
                    <Text style={styles.paymentRequestService}>{message.data.serviceName}</Text>
                    <Text style={styles.paymentRequestAmount}>${message.data.amount}</Text>
                    <Text style={styles.paymentRequestDescription}>{message.data.description}</Text>
                  </View>
                )}
                
                {/* Action buttons for bot messages */}
                {!message.isUser && message.actions && message.actions.length > 0 && (
                  <View style={styles.actionButtonsContainer}>
                    {message.actions.map((action, index) => (
                      <TouchableOpacity
                        key={index}
                        style={[
                          action === '🚀 One-Click Booking' ? styles.oneClickButton : styles.actionButton,
                          action === 'Pay Now' && styles.payButton
                        ]}
                        onPress={() => handleActionPress(action, message.data)}
                      >
                        <Text style={[
                          action === '🚀 One-Click Booking' ? styles.oneClickButtonText : styles.actionButtonText,
                          action === 'Pay Now' && styles.payButtonText
                        ]}>
                          {action}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
            </View>
          ))}
          
          {/* Typing indicator */}
          {isTyping && (
            <View style={[styles.messageContainer, styles.botMessage]}>
              <View style={[styles.messageBubble, styles.botBubble]}>
                <View style={styles.typingIndicator}>
                  <Text style={styles.typingText}>BookAPT is typing</Text>
                  <ActivityIndicator size="small" color="#007AFF" style={styles.typingSpinner} />
                </View>
              </View>
            </View>
          )}
          </ScrollView>
        </View>

        <View style={styles.inputContainer}>
          <AppTextInput
            style={styles.textInput}
            value={inputText}
            onChangeText={setInputText}
            placeholder="Type your message..."
          />
          <TouchableOpacity
            style={styles.sendButton}
            onPress={handleSendMessage}
          >
            <Text style={styles.sendIcon}>→</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Bottom Menu - Positioned absolutely at bottom */}
      <View style={styles.bottomMenuWrapper}>
        <UserBottomMenu
          activeTab="dashboard"
          onTabPress={onTabPress}
          onLogout={onLogout}
        />
      </View>

      {/* Payment Method Selection Modal */}
      <Modal
        visible={showPaymentModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowPaymentModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.paymentModal}>
            <Text style={styles.paymentModalTitle}>Choose Payment Method</Text>
            <Text style={styles.paymentModalSubtitle}>How would you like to pay for your appointment?</Text>
            
            <View style={styles.paymentMethodsList}>
              {availablePaymentMethods.map((method) => (
                <TouchableOpacity
                  key={method.id}
                  style={[
                    styles.paymentMethodItem,
                    selectedPaymentMethod === method.id && styles.paymentMethodSelected
                  ]}
                  onPress={() => handlePaymentMethodSelect(method.id)}
                >
                  <View style={styles.paymentMethodIconWrap}>
                    <PaymentMethodBrandIcon methodId={method.id} size={36} />
                  </View>
                  <Text style={styles.paymentMethodName}>{method.name}</Text>
                  {selectedPaymentMethod === method.id && (
                    <Text style={styles.paymentMethodCheck}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>

            {/* Credit Card Form */}
            {selectedPaymentMethod === 'credit_card' && (
              <View style={styles.creditCardForm}>
                <Text style={styles.formTitle}>Card Details</Text>
                
                <AppTextInput
                  style={styles.input}
                  placeholder="Card Number"
                  value={paymentData.cardNumber}
                  onChangeText={(text) => setPaymentData(prev => ({
                    ...prev,
                    cardNumber: formatCardNumber(text)
                  }))}
                  keyboardType="numeric"
                  maxLength={19}
                />
                
                <View style={styles.row}>
                  <AppTextInput
                    style={[styles.input, styles.halfInput]}
                    placeholder="MM/YY"
                    value={paymentData.expiryDate}
                    onChangeText={(text) => setPaymentData(prev => ({
                      ...prev,
                      expiryDate: formatExpiryDate(text)
                    }))}
                    keyboardType="numeric"
                    maxLength={5}
                  />
                  <AppTextInput
                    style={[styles.input, styles.halfInput]}
                    placeholder="CVC"
                    value={paymentData.cvc}
                    onChangeText={(text) => setPaymentData(prev => ({
                      ...prev,
                      cvc: text.replace(/\D/g, '').slice(0, 4)
                    }))}
                    keyboardType="numeric"
                    maxLength={4}
                  />
                </View>
                
                <AppTextInput
                  style={styles.input}
                  placeholder="Cardholder Name"
                  value={paymentData.cardholderName}
                  onChangeText={(text) => setPaymentData(prev => ({
                    ...prev,
                    cardholderName: text
                  }))}
                />
                
                <AppTextInput
                  style={styles.input}
                  placeholder="ZIP Code"
                  value={paymentData.zipCode}
                  onChangeText={(text) => setPaymentData(prev => ({
                    ...prev,
                    zipCode: text.replace(/\D/g, '').slice(0, 5)
                  }))}
                  keyboardType="numeric"
                  maxLength={5}
                />
              </View>
            )}

            <View style={styles.paymentModalActions}>
              <TouchableOpacity
                style={styles.paymentCancelButton}
                onPress={() => {
                  setShowPaymentModal(false);
                  setPendingBookingData(null);
                  setSelectedPaymentMethod('');
                }}
              >
                <Text style={styles.paymentCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              
              {selectedPaymentMethod === 'credit_card' && (
                <TouchableOpacity
                  style={[
                    styles.paymentConfirmButton,
                    (!paymentData.cardNumber || !paymentData.expiryDate || !paymentData.cvc) && styles.paymentConfirmButtonDisabled
                  ]}
                  onPress={() => handleProcessPayment()}
                  disabled={!paymentData.cardNumber || !paymentData.expiryDate || !paymentData.cvc}
                >
                  <Text style={styles.paymentConfirmButtonText}>Pay & Book Now</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
      
      {/* Appointment Request Form Modal - for businesses NOT on BookAPT */}
      {showAppointmentRequestForm && selectedBusinessForRequest && (
        <Modal
          visible={showAppointmentRequestForm}
          animationType="slide"
          presentationStyle="pageSheet"
          onRequestClose={() => {
            setShowAppointmentRequestForm(false);
            setSelectedBusinessForRequest(null);
          }}
        >
          <AppointmentRequestForm
            business={selectedBusinessForRequest}
            onCancel={() => {
              setShowAppointmentRequestForm(false);
              setSelectedBusinessForRequest(null);
            }}
            onSubmit={() => {
              // Form submission is handled in AppointmentRequestForm
              // Show success message in chat
              const successMessage: Message = {
                id: Date.now().toString(),
                text: `✅ Great! We've sent your appointment request to **${selectedBusinessForRequest.name}**. They're not on BookAPT yet, so we're contacting them on your behalf. We'll notify you as soon as they respond!\n\n💡 If they confirm, they'll also see an invitation to join BookAPT for unlimited bookings.`,
                isUser: false,
                type: 'confirmation',
                timestamp: new Date(),
              };
              setMessages((prev) => [...prev, successMessage]);
              scrollToEnd();
              
              // Close the form
              setShowAppointmentRequestForm(false);
              setSelectedBusinessForRequest(null);
            }}
          />
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  chatArea: {
    flex: 1,
    minHeight: 500, // Increased from 400
  },
  messagesScrollView: {
    flex: 1,
    minHeight: 500, // Increased chat scroll area
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 70, // Space for bottom menu (consistent across all screens)
    flexGrow: 1,
    minHeight: '100%', // Ensure content takes full height even when empty
  },
  header: {
    backgroundColor: 'white',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerLogoContainer: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  headerLogoImage: {
    width: '100%',
    height: '100%',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  messageContainer: {
    marginBottom: 15,
  },
  userMessage: {
    alignItems: 'flex-end',
  },
  botMessage: {
    alignItems: 'flex-start',
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 15,
    borderRadius: 20,
  },
  userBubble: {
    backgroundColor: '#007AFF',
    borderBottomRightRadius: 5,
  },
  botBubble: {
    backgroundColor: 'white',
    borderBottomLeftRadius: 5,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 22,
  },
  userText: {
    color: 'white',
  },
  botText: {
    color: '#333',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    paddingBottom: 10,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
    marginBottom: 70, // Space for bottom menu bar (consistent across all screens)
  },
  bottomMenuWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e1e5e9',
    borderRadius: 20,
    paddingHorizontal: 15,
    paddingVertical: 10,
    marginRight: 10,
    fontSize: 16,
    color: '#333',
  },
  sendButton: {
    backgroundColor: '#f0f0f0',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
    borderWidth: 1,
    borderColor: '#d0d0d0',
  },
  sendIcon: {
    fontSize: 18,
    color: '#333',
    fontWeight: 'bold',
  },
  actionButtonsContainer: {
    marginTop: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  actionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(0, 122, 255, 0.3)',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  actionButtonText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
    textAlign: 'center',
  },
  oneClickButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 25,
    marginRight: 8,
    shadowColor: '#007AFF',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  oneClickButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  typingText: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
  },
  typingSpinner: {
    marginLeft: 4,
  },
  paymentRequestCard: {
    backgroundColor: '#F8F9FA',
    padding: 15,
    borderRadius: 8,
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  paymentRequestTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  paymentRequestService: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 4,
  },
  paymentRequestAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#34C759',
    marginBottom: 8,
  },
  paymentRequestDescription: {
    fontSize: 12,
    color: '#8E8E93',
    lineHeight: 16,
  },
  payButton: {
    backgroundColor: '#34C759',
    borderColor: '#34C759',
  },
  payButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  
  // Payment Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paymentModal: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 24,
    margin: 20,
    maxWidth: 400,
    width: '90%',
    maxHeight: '80%',
  },
  paymentModalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 8,
  },
  paymentModalSubtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
  },
  paymentMethodsList: {
    marginBottom: 20,
  },
  paymentMethodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#e1e5e9',
    marginBottom: 12,
    backgroundColor: '#f8f9fa',
  },
  paymentMethodSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#f0f8ff',
  },
  paymentMethodIconWrap: {
    width: 40,
    height: 40,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentMethodName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    flex: 1,
  },
  paymentMethodCheck: {
    fontSize: 20,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  creditCardForm: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
  },
  formTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfInput: {
    width: '48%',
  },
  paymentModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
    gap: 12,
  },
  paymentCancelButton: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
  },
  paymentCancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#666',
  },
  paymentConfirmButton: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#007AFF',
    alignItems: 'center',
  },
  paymentConfirmButtonDisabled: {
    backgroundColor: '#ccc',
  },
  paymentConfirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default BookAPTChatScreen;